const PRESENTATION_PROPS = [
  "color",
  "background",
  "background-color",
  "font",
  "font-size",
  "font-family",
  "font-weight",
  "line-height",
  "letter-spacing",
  "min-height",
  "height",
];

const EMPTY_BLOCK_TAGS = new Set(["P", "DIV", "SPAN", "H1", "H2", "H3", "H4", "H5", "H6"]);

const stripPresentationRegex = (html) =>
  html
    .replace(
      /\s*(?:color|background(?:-color)?|font(?:-size|-family|-weight)?|line-height|letter-spacing|min-height|height)\s*:\s*[^;"']+(!important)?;?/gi,
      ""
    )
    .replace(/\s*style="\s*"/gi, "")
    .replace(/\s*color="[^"]*"/gi, "")
    .replace(/\s*face="[^"]*"/gi, "")
    .replace(/\s*size="[^"]*"/gi, "")
    .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "");

const stripAllInlineStylesRegex = (html) =>
  html
    .replace(/\s*style\s*=\s*(["'])[\s\S]*?\1/gi, "")
    .replace(/\s*color="[^"]*"/gi, "")
    .replace(/\s*face="[^"]*"/gi, "")
    .replace(/\s*size="[^"]*"/gi, "")
    .replace(/\bisSelectedEnd\b/g, "")
    .replace(/\bisSelectedStart\b/g, "")
    .replace(/\s*class="\s*"/gi, "")
    .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "");

const hasMeaningfulContent = (el) => {
  if (el.querySelector("img, iframe, video, table, ul, ol, blockquote")) {
    return true;
  }

  const clone = el.cloneNode(true);
  clone.querySelectorAll("br").forEach((node) => node.remove());

  const text = (clone.textContent || "").replace(/\u00a0/g, " ").trim();
  return Boolean(text);
};

const isEmptyBlock = (el) => {
  if (!el || el.nodeType !== 1 || !EMPTY_BLOCK_TAGS.has(el.tagName)) {
    return false;
  }

  return !hasMeaningfulContent(el);
};

const removeEmptyBlocks = (root) => {
  [...root.querySelectorAll("p, div, span, h1, h2, h3, h4, h5, h6")].reverse().forEach((el) => {
    if (isEmptyBlock(el)) {
      el.remove();
    }
  });
};

const trimTrailingEmptyBlocks = (root) => {
  let node = root.lastElementChild;

  while (node && isEmptyBlock(node)) {
    const previous = node.previousElementSibling;
    node.remove();
    node = previous;
  }
};

const normalizeCommentDom = (doc) => {
  doc.body.querySelectorAll("[style]").forEach((el) => {
    PRESENTATION_PROPS.forEach((prop) => el.style.removeProperty(prop));
    if (!el.getAttribute("style")?.trim()) {
      el.removeAttribute("style");
    }
  });

  doc.body.querySelectorAll("font").forEach((el) => {
    el.removeAttribute("color");
    el.removeAttribute("face");
    el.removeAttribute("size");
  });

  doc.body.querySelectorAll("jodit").forEach((el) => el.remove());
  removeEmptyBlocks(doc.body);
  trimTrailingEmptyBlocks(doc.body);
};

/**
 * Normalizes comment HTML so theme CSS controls typography and contrast.
 * Strips inline presentation styles and empty Jodit paragraphs from pasted content.
 */
export const stripInlineColors = (html) => {
  if (!html || typeof html !== "string") {
    return html || "";
  }

  if (typeof DOMParser === "undefined") {
    return stripPresentationRegex(html);
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  normalizeCommentDom(doc);
  return doc.body.innerHTML;
};

/**
 * Post excerpt/body only: remove all inline styles from editor/paste HTML.
 * Keeps markup structure; site CSS controls color, weight, and font.
 * Does not change comment rendering (use stripInlineColors there).
 */
export const stripPostInlineStyles = (html) => {
  if (!html || typeof html !== "string") {
    return html || "";
  }

  if (typeof DOMParser === "undefined") {
    return stripAllInlineStylesRegex(html);
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  doc.body.querySelectorAll("[style]").forEach((el) => {
    el.removeAttribute("style");
  });

  doc.body.querySelectorAll("[width], [height]").forEach((el) => {
    const tag = el.tagName.toLowerCase();
    if (tag === "img" || tag === "table" || tag === "td" || tag === "th" || tag === "col" || tag === "iframe" || tag === "video") {
      el.removeAttribute("width");
      el.removeAttribute("height");
    }
  });

  doc.body.querySelectorAll("font").forEach((el) => {
    el.removeAttribute("color");
    el.removeAttribute("face");
    el.removeAttribute("size");
  });

  doc.body.querySelectorAll(".isSelectedEnd, .isSelectedStart").forEach((el) => {
    el.classList.remove("isSelectedEnd", "isSelectedStart");
    if (!el.className.trim()) {
      el.removeAttribute("class");
    }
  });

  doc.body.querySelectorAll("jodit").forEach((el) => el.remove());
  removeEmptyBlocks(doc.body);
  trimTrailingEmptyBlocks(doc.body);

  return doc.body.innerHTML;
};

export const normalizeCommentHtml = stripInlineColors;
